// src/dashboard/pages/teacher/Categories/components/CategoriesFiltersCard.jsx

import React from "react";
import { FaList, FaSearch, FaSyncAlt, FaTable } from "react-icons/fa";

export default function CategoriesFiltersCard({
  filters,
  setFilters,
  loading,
  groupOptions = [],
  seasonOptions = [],
  yearOptions = [],
  ageCategoryOptions = [],
  onRefresh,
}) {
  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [key]: value,
      };

      // Si l’enseignant veut voir les adhérents sans poids,
      // la vue adhérents est beaucoup plus adaptée que la vue répartition.
      if (key === "weightStatus" && value === "without") {
        next.viewMode = "members";
      }

      return next;
    });
  };

  const resetFilters = () => {
    setFilters((prev) => ({
      ...prev,
      genre: "Tous",
      year: "Tous",
      group: "",
      ageCategory: "Tous",
      query: "",
      sortBy: "name-asc",
      weightStatus: "with",
      onlyFilledCategories: true,
    }));
  };

  const hasFilters =
    filters.genre !== "Tous" ||
    filters.year !== "Tous" ||
    filters.group ||
    filters.ageCategory !== "Tous" ||
    String(filters.query || "").trim() ||
    filters.sortBy !== "name-asc" ||
    filters.weightStatus !== "with" ||
    !filters.onlyFilledCategories;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Recherche & filtres
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Affiner la répartition
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Par défaut, seuls les adhérents avec un poids renseigné sont
              affichés. Vous pouvez aussi afficher les adhérents à peser.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => updateFilter("viewMode", "distribution")}
              className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold transition ${
                filters.viewMode === "distribution"
                  ? "bg-[#013E8E] text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              }`}
            >
              <FaTable />
              Répartition
            </button>

            <button
              type="button"
              onClick={() => updateFilter("viewMode", "members")}
              className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold transition ${
                filters.viewMode === "members"
                  ? "bg-[#013E8E] text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              }`}
            >
              <FaList />
              Adhérents
            </button>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-4 xl:grid-cols-7">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Recherche
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={filters.query}
                onChange={(event) => updateFilter("query", event.target.value)}
                placeholder="Nom, prénom, groupe..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Saison
            </label>

            <select
              value={filters.season}
              onChange={(event) => updateFilter("season", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              {seasonOptions.map((season) => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Groupe
            </label>

            <select
              value={filters.group}
              onChange={(event) => updateFilter("group", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Tous les groupes</option>
              {groupOptions.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Genre
            </label>

            <select
              value={filters.genre}
              onChange={(event) => updateFilter("genre", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="Tous">Tous</option>
              <option value="Masculin">Masculin</option>
              <option value="Féminin">Féminin</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Année
            </label>

            <select
              value={filters.year}
              onChange={(event) => updateFilter("year", event.target.value)}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="Tous">Toutes</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Catégorie
            </label>

            <select
              value={filters.ageCategory}
              onChange={(event) =>
                updateFilter("ageCategory", event.target.value)
              }
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="Tous">Toutes catégories</option>
              {ageCategoryOptions.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Poids
            </label>

            <select
              value={filters.weightStatus}
              onChange={(event) =>
                updateFilter("weightStatus", event.target.value)
              }
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="with">Avec poids</option>
              <option value="without">Sans poids</option>
              <option value="all">Tous</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {filters.viewMode === "distribution" ? (
            <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={filters.onlyFilledCategories}
                onChange={(event) =>
                  updateFilter("onlyFilledCategories", event.target.checked)
                }
                className="h-4 w-4 rounded border-slate-300 accent-[#013E8E]"
              />
              Afficher uniquement les catégories avec adhérents
            </label>
          ) : (
            <span className="text-sm text-slate-500 dark:text-slate-400">
              La vue adhérents permet de peser rapidement les judokas en début
              de saison.
            </span>
          )}

          {filters.viewMode === "members" ? (
            <select
              value={filters.sortBy}
              onChange={(event) => updateFilter("sortBy", event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 lg:w-auto"
            >
              <option value="name-asc">Nom A → Z</option>
              <option value="name-desc">Nom Z → A</option>
              <option value="weight-asc">Poids croissant</option>
              <option value="weight-desc">Poids décroissant</option>
              <option value="category-asc">Catégorie A → Z</option>
            </select>
          ) : null}
        </div>

        {hasFilters ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}